// @flow strict
import React, { useEffect } from 'react'
import gatsby, { graphql, withPrefix } from 'gatsby'
import {
  ContentWrapper,
  SEOHead,
  type Location,
  PostListing,
} from '@mondough/public-web-components'

import {
  type singleCategory as ContentfulCategory,
  type singleCategory_contentfulWebBlogCategory_posts as CategoryPost,
} from '../../__types__/singleCategory'

import HeroHeader from '../components/HeroHeader'
import Pagination from '../components/Pagination'

import setDimensionsFirePageview from '../utils/googleAnalytics.js'
import { slugifyString } from '../utils/stringUtils/es6'
import Global from '../Global'

type PageContext = {
  limit: number,
  skip: number,
  numPages: number,
  currentPage: number,
  posts: Array<CategoryPost>,
  basePath: string,
}

type Props = gatsby.PageRendererProps & {
  data: ContentfulCategory,
  pageContext: PageContext,
  location: Location,
}

const Category = ({ data, pageContext, location: { pathname } }: Props) => {
  const { name, description } = data.contentfulWebBlogCategory || {}
  const { posts, currentPage, numPages, basePath } = pageContext

  const url = `https://monzo.com${withPrefix(basePath)}`
  const ogDescriptionText = `Category ${name} on the Monzo blog`
  const isUSPage = slugifyString(name) === 'usa'

  useEffect(() => {
    setDimensionsFirePageview(
      {
        dimension4: 'category',
        dimension5: name,
      },
      pathname,
    )
  })

  return (
    <Global
      pageMetadata={
        <SEOHead
          pageTitle={`${name} - Monzo Blog`}
          pageUrl={url}
          description={ogDescriptionText}
          locale={isUSPage ? 'en-US' : 'en-GB'}
        />
      }
      isUSPage={isUSPage}
    >
      <HeroHeader
        title={name}
        subtitle={description && description.description}
      />
      <ContentWrapper>
        <PostListing posts={posts} />
      </ContentWrapper>
      <Pagination
        currentPage={currentPage}
        numPages={numPages}
        basePath={basePath}
      />
    </Global>
  )
}

export default Category

export const query = graphql`
  query singleCategory($id: String!) {
    contentfulWebBlogCategory(id: { eq: $id }) {
      id
      name: categoryName
      description {
        description
      }
      posts: web___blog_post {
        id
        title: blogTitle
        description {
          text: description
        }
        webSlug
        startDate
        createdAt
        category {
          categoryName
        }
        ogImageUrl
        ogImage {
          file {
            url
          }
          description
        }
      }
    }
  }
`
